import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaChartLine, FaSearch, FaUserGraduate, FaStar, FaCheck, FaTimes } from 'react-icons/fa';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, RadialLinearScale } from 'chart.js';
import { Line, Radar } from 'react-chartjs-2';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../common/LoadingSpinner';

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, RadialLinearScale);

/**
 * Mentorship tracking component for tracking student progress
 */
const MentorshipTracking = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { showError } = useToast();
  
  // Fetch students on component mount
  useEffect(() => {
    fetchStudents();
  }, []);
  
  // Fetch students from API
  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await api.get('/mentors/children');
      setStudents(response.data.data);
      
      // Select first student by default if available
      if (response.data.data.length > 0) {
        setSelectedStudent(response.data.data[0]);
        fetchProgressData(response.data.data[0]._id);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      showError('Failed to load students');
      setLoading(false);
    }
  };
  
  // Fetch progress data for a specific student
  const fetchProgressData = async (studentId) => {
    try {
      setLoading(true);
      const response = await api.get(`/mentors/children/${studentId}/progress`);
      setProgressData(response.data.data);
    } catch (error) {
      console.error('Error fetching progress data:', error);
      showError('Failed to load progress data');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle student selection
  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
    fetchProgressData(student._id);
  };
  
  // Filter students based on search term
  const filteredStudents = students.filter((student) => {
    const search = searchTerm.toLowerCase();
    return (
      `${student.firstName} ${student.lastName}`.toLowerCase().includes(search)
    );
  });
  
  // Default progress data if API data is not available
  const defaultProgressData = {
    sessionAttendance: 90,
    completedTasks: 85,
    skillDevelopment: [7, 8, 8, 9, 8, 7, 9],
    recentProgress: [
      { date: '2023-01-15', score: 6 },
      { date: '2023-02-15', score: 7 },
      { date: '2023-03-15', score: 7 },
      { date: '2023-04-15', score: 8 },
      { date: '2023-05-15', score: 9 },
      { date: '2023-06-15', score: 8 }
    ],
    lastAssessment: {
      date: '2023-06-15',
      metrics: {
        skillDevelopment: 8,
        completedTasks: 9,
        consistencyEffort: 8,
        communication: 7,
        initiativeProblemSolving: 8,
        growthReflection: 9,
        collaborationTeamwork: 7,
        behaviorAttitude: 9,
        learningDevelopment: 8,
        personalGrowth: 9
      },
      comment: "Making excellent progress in problem-solving skills. Shows great initiative in tackling new challenges."
    },
    strengthAreas: ['Problem Solving', 'Initiative', 'Attitude'],
    improvementAreas: ['Communication', 'Teamwork']
  };
  
  const progressDataForCharts = progressData || defaultProgressData;
  
  // Progress trend chart data
  const progressTrendData = {
    labels: progressDataForCharts.recentProgress.map(p => new Date(p.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })),
    datasets: [
      {
        label: 'Overall Progress',
        data: progressDataForCharts.recentProgress.map(p => p.score),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        tension: 0.3,
      },
    ],
  };
  
  const progressTrendOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: 'Progress Over Time',
        font: {
          size: 14,
        },
      },
    },
    scales: {
      y: {
        min: 0,
        max: 10,
        ticks: {
          stepSize: 1,
        }
      },
    },
  };
  
  // Skills radar chart data
  const skillsRadarData = {
    labels: [
      'Skill Development',
      'Task Completion',
      'Consistency/Effort',
      'Communication',
      'Initiative/Problem Solving',
      'Growth/Reflection',
      'Collaboration',
      'Behavior/Attitude',
      'Learning Development',
      'Personal Growth'
    ],
    datasets: [
      {
        label: 'Current Assessment',
        data: [
          progressDataForCharts.lastAssessment.metrics.skillDevelopment,
          progressDataForCharts.lastAssessment.metrics.completedTasks,
          progressDataForCharts.lastAssessment.metrics.consistencyEffort,
          progressDataForCharts.lastAssessment.metrics.communication,
          progressDataForCharts.lastAssessment.metrics.initiativeProblemSolving,
          progressDataForCharts.lastAssessment.metrics.growthReflection,
          progressDataForCharts.lastAssessment.metrics.collaborationTeamwork,
          progressDataForCharts.lastAssessment.metrics.behaviorAttitude,
          progressDataForCharts.lastAssessment.metrics.learningDevelopment,
          progressDataForCharts.lastAssessment.metrics.personalGrowth
        ],
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderColor: 'rgb(54, 162, 235)',
        pointBackgroundColor: 'rgb(54, 162, 235)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgb(54, 162, 235)'
      }
    ],
  };
  
  const skillsRadarOptions = {
    scales: {
      r: {
        min: 0,
        max: 10,
        ticks: {
          stepSize: 2
        }
      }
    }
  };
  
  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <FaChartLine className="mr-2 text-primary" /> Mentorship Tracking
          </h1>
          <p className="text-gray-600 mt-1">
            Track the progress of your mentored students
          </p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Students List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-soft p-4">
            <div className="relative mb-4">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="text-gray-400" />
              </div>
              <input
                type="text"
                className="form-input pl-10 py-2 w-full"
                placeholder="Search students..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <h2 className="text-lg font-semibold mb-3">Your Students</h2>
            
            {filteredStudents.length === 0 ? (
              <div className="text-center py-4">
                <FaUserGraduate className="mx-auto text-gray-300 text-4xl mb-2" />
                <p className="text-gray-500">No students found</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {filteredStudents.map((student) => (
                  <li key={student._id}>
                    <button
                      className={`w-full text-left py-3 px-2 hover:bg-gray-50 ${selectedStudent && selectedStudent._id === student._id ? 'bg-blue-50' : ''}`}
                      onClick={() => handleStudentSelect(student)}
                    >
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-primary text-white flex items-center justify-center mr-3">
                          {student.firstName.charAt(0)}
                          {student.lastName.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {student.firstName} {student.lastName}
                          </p>
                          <p className="text-xs text-gray-500">
                            {student.grade || 'Grade not specified'}
                          </p>
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        
        {/* Progress Dashboard */}
        <div className="lg:col-span-3">
          {loading ? (
            <div className="bg-white rounded-lg shadow-soft p-6 flex justify-center min-h-[400px] items-center">
              <LoadingSpinner size="lg" />
            </div>
          ) : !selectedStudent ? (
            <div className="bg-white rounded-lg shadow-soft p-6 text-center min-h-[400px] flex flex-col justify-center items-center">
              <FaUserGraduate className="text-gray-300 text-5xl mb-4" />
              <h3 className="text-xl font-medium text-gray-700 mb-2">No Student Selected</h3>
              <p className="text-gray-500 mb-4">
                Select a student from the list to view their progress
              </p>
            </div>
          ) : (
            <>
              {/* Student Overview */}
              <div className="bg-white rounded-lg shadow-soft p-6 mb-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="h-12 w-12 rounded-full bg-primary text-white flex items-center justify-center mr-4 text-lg">
                      {selectedStudent.firstName.charAt(0)}
                      {selectedStudent.lastName.charAt(0)}
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold">
                        {selectedStudent.firstName} {selectedStudent.lastName}
                      </h2>
                      <p className="text-gray-600">
                        {selectedStudent.grade || 'Grade not specified'} • Mentoring since {new Date(selectedStudent.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 md:mt-0">
                    <Link
                      to={`/mentor/sessions/new?studentId=${selectedStudent._id}`}
                      className="btn-primary"
                    >
                      Schedule Session
                    </Link>
                  </div>
                </div>
                
                {/* Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-gray-500 text-sm mb-1">Session Attendance</p>
                    <div className="flex items-center">
                      <div className="text-2xl font-bold mr-2">{progressDataForCharts.sessionAttendance}%</div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div 
                          className="bg-blue-600 h-2.5 rounded-full" 
                          style={{ width: `${progressDataForCharts.sessionAttendance}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-gray-500 text-sm mb-1">Completed Tasks</p>
                    <div className="flex items-center">
                      <div className="text-2xl font-bold mr-2">{progressDataForCharts.completedTasks}%</div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div 
                          className="bg-green-600 h-2.5 rounded-full" 
                          style={{ width: `${progressDataForCharts.completedTasks}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <p className="text-gray-500 text-sm mb-1">Last Assessment</p>
                    <div className="flex items-center">
                      <div className="flex items-center mr-2">
                        <FaStar className="text-yellow-500 mr-1" />
                        <span className="text-2xl font-bold">
                          {Object.values(progressDataForCharts.lastAssessment.metrics).reduce((a, b) => a + b, 0) / 
                           Object.values(progressDataForCharts.lastAssessment.metrics).length}
                        </span>
                        <span className="text-gray-500 text-sm">/10</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(progressDataForCharts.lastAssessment.date).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Progress Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="bg-white rounded-lg shadow-soft p-6">
                  <Line options={progressTrendOptions} data={progressTrendData} height={100} />
                </div>
                
                <div className="bg-white rounded-lg shadow-soft p-6">
                  <h3 className="text-lg font-semibold mb-4 text-center">Skill Assessment</h3>
                  <Radar data={skillsRadarData} options={skillsRadarOptions} />
                </div>
              </div>
              
              {/* Strengths and Areas for Improvement */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="bg-white rounded-lg shadow-soft p-6">
                  <h3 className="text-lg font-semibold mb-4">Strengths</h3>
                  <ul className="space-y-2">
                    {progressDataForCharts.strengthAreas.map((strength, index) => (
                      <li key={index} className="flex items-start">
                        <FaCheck className="text-green-500 mt-1 mr-2" />
                        <span>{strength}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-white rounded-lg shadow-soft p-6">
                  <h3 className="text-lg font-semibold mb-4">Areas for Improvement</h3>
                  <ul className="space-y-2">
                    {progressDataForCharts.improvementAreas.map((area, index) => (
                      <li key={index} className="flex items-start">
                        <FaTimes className="text-red-500 mt-1 mr-2" />
                        <span>{area}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              {/* Last Assessment Comment */}
              <div className="bg-white rounded-lg shadow-soft p-6">
                <h3 className="text-lg font-semibold mb-4">Last Assessment</h3>
                <p className="text-gray-600">
                  <span className="text-gray-400 text-sm block mb-2">
                    {new Date(progressDataForCharts.lastAssessment.date).toLocaleDateString('en-US', { 
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                  {progressDataForCharts.lastAssessment.comment}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MentorshipTracking;
